import { z } from "zod";
import { AUDIT_ENTITIES, ROLES, ZVOOVE_MODES } from "../enums.js";
import { FEDERAL_STATES } from "../holidays.js";
import { phoneSchema } from "../phone-schema.js";
import { idSchema, isoTimestampSchema } from "./common.js";
import { schedulesSchema } from "./sync.js";

// Staff accounts (dispatchers and admins) ------------------------------------------------------

export const STAFF_ROLES = ["dispatcher", "admin"] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

export const PASSWORD_MIN_LENGTH = 8;
export const NAME_MAX_LENGTH = 100;

export const emailSchema = z.string().trim().toLowerCase().email().max(200);
export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(200);
export const nameSchema = z.string().trim().min(1).max(NAME_MAX_LENGTH);

export const adminUserSchema = z.object({
  id: z.string(),
  role: z.enum(ROLES),
  email: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  lastLoginAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const adminUsersResponseSchema = z.object({ users: z.array(adminUserSchema) });
export type AdminUsersResponse = z.infer<typeof adminUsersResponseSchema>;

export const createUserBodySchema = z.object({
  role: z.enum(STAFF_ROLES),
  email: emailSchema,
  name: nameSchema,
  password: passwordSchema,
});
export type CreateUserBody = z.infer<typeof createUserBodySchema>;

export const updateUserBodySchema = z.object({
  name: nameSchema.optional(),
  role: z.enum(STAFF_ROLES).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateUserBody = z.infer<typeof updateUserBodySchema>;

export const resetPasswordBodySchema = z.object({ password: passwordSchema });
export type ResetPasswordBody = z.infer<typeof resetPasswordBodySchema>;

export const userIdParamsSchema = z.object({ id: idSchema });

// Employee logins ------------------------------------------------------------------------------

export const employeeUserRowSchema = z.object({
  zvEmployeeId: z.number().int(),
  firstName: z.string(),
  lastName: z.string(),
  personnelNo: z.number().int().nullable(),
  zvEmail: z.string().nullable(),
  // The mirror's phone as zvoove stores it (not normalised), extension 3.
  zvPhone: z.string().nullable(),
  isEmployeeActive: z.boolean(),
  login: z
    .object({
      userId: z.string(),
      email: z.string().nullable(),
      // E.164, extension 3.
      phone: z.string().nullable(),
      isActive: z.boolean(),
      lastLoginAt: isoTimestampSchema.nullable(),
    })
    .nullable(),
});
export type EmployeeUserRow = z.infer<typeof employeeUserRowSchema>;

export const employeeUsersResponseSchema = z.object({ employees: z.array(employeeUserRowSchema) });
export type EmployeeUsersResponse = z.infer<typeof employeeUsersResponseSchema>;

// Creates the login or changes its channels: only the fields sent are changed. `phone: null`
// removes the phone number (only while an e-mail remains). A new login needs at least one.
export const employeeUserBodySchema = z
  .object({
    zvEmployeeId: z.number().int().positive(),
    email: emailSchema.optional(),
    phone: phoneSchema.nullable().optional(),
  })
  .refine((b) => b.email !== undefined || b.phone !== undefined, {
    message: "email_or_phone",
    path: ["email"],
  });
export type EmployeeUserBody = z.infer<typeof employeeUserBodySchema>;

// Settings -------------------------------------------------------------------------------------

export const absenceTypeOptionSchema = z.object({ key: z.string(), name: z.string() });
export type AbsenceTypeOption = z.infer<typeof absenceTypeOptionSchema>;

export const settingsValuesSchema = z.object({
  absenceTypeVacation: z.string().trim().min(1).max(10),
  absenceTypeSick: z.string().trim().min(1).max(10),
  // Extension 2: key of AZK requests ("FA") and the sick-report switch (default off).
  absenceTypeAzk: z.string().trim().min(1).max(10),
  sickReportsEnabled: z.boolean(),
  holidayState: z.enum(FEDERAL_STATES),
  // Empty string = no alert mails.
  alertEmail: z.union([z.literal(""), emailSchema]),
  // zvoove API package: calls per month and the share held back for outbound jobs and manual
  // runs (extension 1).
  callBudgetPerMonth: z.number().int().min(1).max(1_000_000),
  callReservePercent: z.number().int().min(0).max(90),
  schedules: schedulesSchema,
});
export type SettingsValues = z.infer<typeof settingsValuesSchema>;

export const settingsResponseSchema = settingsValuesSchema.extend({
  updatedAt: isoTimestampSchema.nullable(),
  // From the cache the monthly full sync and the refresh button fill; empty until the first
  // fetch (`absenceTypesFetchedAt` null).
  absenceTypes: z.array(absenceTypeOptionSchema),
  absenceTypesFetchedAt: isoTimestampSchema.nullable(),
  // Days of the current Berlin month, for the per-day hint next to the budget fields.
  daysInMonth: z.number().int(),
  zvooveMode: z.enum(ZVOOVE_MODES),
  writeAllowedEmployeeIds: z.union([z.array(z.number().int()), z.literal("all")]),
});
export type SettingsResponse = z.infer<typeof settingsResponseSchema>;

export type SettingsUpdateBody = SettingsValues;

// Audit ----------------------------------------------------------------------------------------

export const auditEntrySchema = z.object({
  id: z.string(),
  entity: z.enum(AUDIT_ENTITIES),
  entityId: z.string(),
  action: z.string(),
  actorId: z.string(),
  // Display name of the actor; null for `system` and deleted users.
  actorName: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  at: isoTimestampSchema,
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditQuerySchema = z.object({
  entity: z.enum(AUDIT_ENTITIES),
  entityId: z.string().min(1).max(64),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

export const auditParamsSchema = z.object({
  entity: z.enum(AUDIT_ENTITIES),
  id: z.string().min(1).max(64),
});

export const auditLimitQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const auditResponseSchema = z.object({ entries: z.array(auditEntrySchema) });
export type AuditResponse = z.infer<typeof auditResponseSchema>;
